import React, { useState, useEffect } from 'react';
import API from '../api/axios';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import PageLoader from '../components/PageLoader';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiX, FiFileText, FiDownload, FiPrinter,
} from 'react-icons/fi';
import './QuotationMgmt.css'; // reuse the existing quo-* styles
import './CustomerOrders.css'; // reuse co-* form styles for the modal

const PAGE_SIZE = 8;

const STATUS_CFG = {
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9', border: '#e2e8f0' },
  sent:  { label: 'Sent',  color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
  paid:  { label: 'Paid',  color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
};

function StatusBadge({ status }) {
  const c = STATUS_CFG[status] || STATUS_CFG.draft;
  return <span className="quo-badge" style={{ color: c.color, background: c.bg, border: `1px solid ${c.border}` }}>{c.label}</span>;
}

function ConfirmDialog({ title, message, confirmLabel = 'Confirm', confirmColor = '#2563eb', icon, onConfirm, onCancel }) {
  return (
    <div className="quo-confirm-overlay" onClick={onCancel}>
      <div className="quo-confirm-box" onClick={e => e.stopPropagation()}>
        {icon && <div className="quo-confirm-icon">{icon}</div>}
        <h4 className="quo-confirm-title">{title}</h4>
        <p className="quo-confirm-msg">{message}</p>
        <div className="quo-confirm-actions">
          <button className="quo-confirm-cancel" onClick={onCancel}>Cancel</button>
          <button className="quo-confirm-ok" style={{ background: confirmColor }} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
function fmt(iso) { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
function fmtINR(n) { if (!n && n !== 0) return '—'; return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmt2(n) { return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function itemsSubtotal(inv) {
  return (inv.items || []).reduce((s, it) => s + ((+it.qty || 0) * (+it.rate || 0)), 0);
}
function invoiceTotals(inv) {
  const subtotal = itemsSubtotal(inv);
  const cgst = subtotal * (+inv.cgstPercent || 0) / 100;
  const sgst = subtotal * (+inv.sgstPercent || 0) / 100;
  const igst = subtotal * (+inv.igstPercent || 0) / 100;
  const rawTotal = subtotal + cgst + sgst + igst;
  const grandTotal = rawTotal + (+inv.roundOff || 0);
  return { subtotal, cgst, sgst, igst, grandTotal };
}

function freshItem() { return { description: '', hsnCode: '', qty: '', rate: '' }; }

export default function CustomerInvoiceMgmt() {
  const [list, setList]             = useState([]);
  const [selected, setSelected]     = useState(null);
  const [search, setSearch]         = useState('');
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [editInv, setEditInv]       = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await API.get('/customer-invoices');
      setList(r.data);
      setSelected(prev => prev ? (r.data.find(i => i._id === prev._id) || r.data[0] || null) : (r.data[0] || null));
    } catch { toast.error('Failed to load customer invoices'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = list.filter(i => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (i.invoiceNumber || '').toLowerCase().includes(s) || (i.clientName || i.client?.name || '').toLowerCase().includes(s);
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  async function updateStatus(inv, status) {
    try {
      const r = await API.put(`/customer-invoices/${inv._id}/status`, { status });
      await load();
      setSelected(r.data);
      toast.success(`Marked ${status}`);
    } catch { toast.error('Failed to update status'); }
  }

  async function deleteInv(id) {
    try {
      await API.delete(`/customer-invoices/${id}`);
      setConfirmDel(null);
      const next = list.filter(i => i._id !== id);
      setList(next);
      setSelected(next[0] || null);
      toast.success('Invoice deleted');
    } catch { toast.error('Failed to delete invoice'); }
  }

  const INVOICE_STYLE = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Source+Serif+4:wght@400;600;700&display=swap');
    *{box-sizing:border-box}
    body{font-family:'Inter',Arial,sans-serif;padding:0;color:#1e293b;font-size:11.5px;-webkit-font-smoothing:antialiased}
    .sheet{border:1.5px solid #1e3a5f;border-radius:6px;margin:16px;background:#fff;overflow:hidden}
    table{width:100%;border-collapse:collapse}
    td,th{border:1px solid #cbd5e1;padding:8px 12px;vertical-align:top;line-height:1.5}
    .hd-left{width:55%}
    .co-name{font-family:'Source Serif 4',Georgia,serif;font-size:21px;font-weight:700;color:#1e3a5f;letter-spacing:.2px}
    .co-line{font-size:10.5px;margin-top:3px;color:#475569}
    .co-hd{display:flex;gap:12px;align-items:flex-start}
    .co-hd img{height:52px;width:auto;object-fit:contain}
    .hd-right table{border:none;table-layout:fixed}
    .hd-right td{border:none;padding:0}
    .hd-right>table>tbody>tr>td{border:1px solid #cbd5e1;padding:7px 12px;font-size:10.8px;color:#334155;width:50%}
    .field-label{display:block;font-size:9px;text-transform:uppercase;letter-spacing:.5px;color:#94a3b8;font-weight:600;margin-bottom:1px}
    th{background:#1e3a5f;color:#fff;font-size:10px;text-transform:uppercase;letter-spacing:.4px;font-weight:600;text-align:center;border-color:#1e3a5f}
    td.c{text-align:center}
    .cust-name{font-family:'Source Serif 4',Georgia,serif;font-size:13px;font-weight:700;color:#0f172a}
    .money-row td{background:#f8fafc;color:#334155}
    .total-row td{font-weight:700;background:#eef2f7;color:#0f172a;font-size:12.5px}
    .grand-label{letter-spacing:.4px}
    .amt-title{font-size:9px;text-transform:uppercase;letter-spacing:.5px;color:#94a3b8;font-weight:600;display:block;margin-bottom:3px}
    .bank-title{font-size:9px;text-transform:uppercase;letter-spacing:.5px;color:#94a3b8;font-weight:600;display:block;margin-bottom:3px}
    .bank-name{font-family:'Source Serif 4',Georgia,serif;font-weight:700;color:#0f172a;font-size:12px}
    .decl-title{font-family:'Source Serif 4',Georgia,serif;font-weight:700;color:#0f172a;font-size:12px;display:block;margin-bottom:3px}
    .decl-text{color:#475569;font-size:10.5px}
    .for-co{font-family:'Source Serif 4',Georgia,serif;font-weight:700;color:#1e3a5f;font-size:12.5px}
    .sig-line{color:#94a3b8;font-size:10px}
    @media print{.sheet{margin:0}}
  `;

  function buildInvoiceSheetHTML(inv) {
    const { subtotal, cgst, sgst, igst, grandTotal } = invoiceTotals(inv);

    const itemRows = (inv.items || []).map((it, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${it.description || ''}</td>
        <td class="c">${it.hsnCode || ''}</td>
        <td class="c">${it.qty ?? ''}</td>
        <td class="c">₹${fmt2(it.rate)}</td>
        <td class="c">₹${fmt2((+it.qty || 0) * (+it.rate || 0))}</td>
      </tr>`).join('');

    return `
      <div class="sheet">
        <table>
          <tr>
            <td class="hd-left" style="padding:0">
              <table style="border:none">
                <tr>
                  <td style="border:none">
                    <div class="co-hd">
                      <img src="${window.location.origin}/cmpy_logo.png" alt="Logo" onerror="this.style.display='none'" />
                      <div>
                        <div class="co-name">Creative Glass Tec</div>
                        <div class="co-line">No:16, Nehru Street, Sakthi Vellammal Nagar, By Pass Road, Madurai - 625003.</div>
                        <div class="co-line">GST No: 33BCVPM9995C2ZT</div>
                        <div class="co-line">Email: creativeglasstec77@gmail.com</div>
                        <div class="co-line">Mobile: 98438 88077</div>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style="border:none;border-top:1px solid #cbd5e1;padding-top:10px">
                    <span class="field-label">Bill To</span>
                    <div class="cust-name">${(inv.clientName || inv.client?.name || '—')}</div>
                    <div class="co-line">${(inv.clientAddress || '').replace(/\n/g, '<br/>')}</div>
                    ${inv.clientGSTIN ? `<div class="co-line">GSTIN: ${inv.clientGSTIN}</div>` : ''}
                  </td>
                </tr>
              </table>
            </td>
            <td class="hd-right" style="padding:0">
              <table>
                <tr><td><span class="field-label">Invoice No.</span><b>${inv.invoiceNumber}</b></td><td><span class="field-label">Dated</span><b>${fmt(inv.invoiceDate)}</b></td></tr>
                <tr><td colspan="2"><span class="field-label">Delivery Note</span>${inv.deliveryNote || '&nbsp;'}</td></tr>
                <tr><td><span class="field-label">Supplier's Ref.</span>${inv.suppliersRef || '&nbsp;'}</td><td><span class="field-label">Other Reference</span>${inv.otherReference || '&nbsp;'}</td></tr>
                <tr><td><span class="field-label">Buyers Order No.</span>${inv.buyersOrderNo || '&nbsp;'}</td><td><span class="field-label">Dated</span>${inv.buyersOrderDate ? fmt(inv.buyersOrderDate) : '&nbsp;'}</td></tr>
                <tr><td><span class="field-label">Dispatch Document No.</span>${inv.dispatchDocNo || '&nbsp;'}</td><td><span class="field-label">Vehicle No.</span>${inv.vehicleNo || '&nbsp;'}</td></tr>
                <tr><td style="text-align:center"><span class="field-label" style="display:block;text-align:center">Dispatched Through</span>${inv.dispatchedThrough || '&nbsp;'}</td><td style="text-align:center"><span class="field-label" style="display:block;text-align:center">Destination</span>${inv.destination || '&nbsp;'}</td></tr>
              </table>
            </td>
          </tr>
        </table>

        <table style="margin-top:-1px">
          <thead>
            <tr><th style="width:6%">SL No.</th><th>Description of Goods and Service</th><th style="width:10%">HSN/SAC</th><th style="width:10%">Qty/Sqft</th><th style="width:14%">Rate</th><th style="width:16%">Total</th></tr>
          </thead>
          <tbody>
            ${itemRows}
            <tr class="money-row"><td colspan="5" style="text-align:right;font-weight:600">Total</td><td class="c" style="font-weight:600">₹${fmt2(subtotal)}</td></tr>
            ${+inv.cgstPercent > 0 ? `<tr class="money-row"><td colspan="5" style="text-align:right">CGST @ ${inv.cgstPercent}%</td><td class="c">₹${fmt2(cgst)}</td></tr>` : ''}
            ${+inv.sgstPercent > 0 ? `<tr class="money-row"><td colspan="5" style="text-align:right">SGST @ ${inv.sgstPercent}%</td><td class="c">₹${fmt2(sgst)}</td></tr>` : ''}
            ${+inv.igstPercent > 0 ? `<tr class="money-row"><td colspan="5" style="text-align:right">IGST @ ${inv.igstPercent}%</td><td class="c">₹${fmt2(igst)}</td></tr>` : ''}
            <tr class="money-row"><td colspan="5" style="text-align:right">Round Off</td><td class="c">₹${fmt2(inv.roundOff)}</td></tr>
            <tr class="total-row"><td colspan="5" style="text-align:right" class="grand-label">GRAND TOTAL</td><td class="c">₹${fmt2(grandTotal)}</td></tr>
          </tbody>
        </table>

        <table style="margin-top:-1px">
          <tr>
            <td style="width:50%">
              <span class="amt-title">Amount Chargeable (in words)</span>
              ${inv.amountInWords || '—'}
            </td>
            <td>
              <span class="bank-title">Bank Details</span>
              <div class="bank-name">Creative Glass Tec</div>
              <div class="co-line">Tamilnadu Mercantile Bank Ltd</div>
              <div class="co-line">Account No: 093539843888077</div>
              <div class="co-line">IFSC Code: TMBL0000093</div>
              <div class="co-line">Branch: S S Colony, Madurai</div>
            </td>
          </tr>
          <tr>
            <td>
              <span class="decl-title">Declaration</span>
              <span class="decl-text">We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</span>
              <br/><br/><br/>
              <span class="sig-line">Signature of Transporter / Receiver</span>
            </td>
            <td style="text-align:right">
              <span class="for-co">For Creative Glass Tec</span>
              <br/><br/><br/>
              <span class="sig-line">Authorised Signature</span>
            </td>
          </tr>
        </table>
      </div>`;
  }

  function printInvoice() {
    if (!selected) return;
    const inv = selected;
    const win = window.open('', '_blank');
    win.document.write(`<!DOCTYPE html><html><head><title>${inv.invoiceNumber}</title><style>${INVOICE_STYLE}</style></head><body>
      ${buildInvoiceSheetHTML(inv)}
      <script>window.onload=()=>{window.print();}${'<'}/script>
    </body></html>`);
    win.document.close();
  }

  async function downloadInvoicePDF() {
    if (!selected) return;
    const inv = selected;
    const holder = document.createElement('div');
    holder.style.position = 'fixed';
    holder.style.left = '-10000px';
    holder.style.top = '0';
    holder.style.width = '900px';
    holder.innerHTML = `<style>${INVOICE_STYLE}</style>${buildInvoiceSheetHTML(inv)}`;
    document.body.appendChild(holder);
    try {
      // Let the logo <img> and web fonts finish loading before rasterizing.
      const img = holder.querySelector('img');
      if (img && !img.complete) {
        await new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
      }
      if (document.fonts?.ready) {
        try { await document.fonts.ready; } catch { /* ignore */ }
      }
      const canvas = await html2canvas(holder, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight <= pageHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      } else {
        // Slice tall content across multiple pages.
        let renderedHeight = 0;
        const pxPerPt = canvas.width / imgWidth;
        while (renderedHeight < imgHeight) {
          const sliceHeightPt = Math.min(pageHeight, imgHeight - renderedHeight);
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceHeightPt * pxPerPt;
          sliceCanvas.getContext('2d').drawImage(
            canvas, 0, renderedHeight * pxPerPt, canvas.width, sliceCanvas.height, 0, 0, canvas.width, sliceCanvas.height
          );
          if (renderedHeight > 0) pdf.addPage();
          pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', 0, 0, imgWidth, sliceHeightPt);
          renderedHeight += sliceHeightPt;
        }
      }
      pdf.save(`INV-${inv.invoiceNumber}.pdf`);
    } catch {
      toast.error('Failed to generate PDF');
    } finally {
      document.body.removeChild(holder);
    }
  }

  return (
    <Layout>
      {loading && <PageLoader text="Loading customer invoices..." />}
      <div className="quo-root">
        <div className="quo-layout">
          {/* LEFT LIST */}
          <div className="quo-list-col">
            <div className="quo-list-header">
              <span className="quo-list-title">Customer Invoices <span className="quo-count">{filtered.length}</span></span>
            </div>
            <div className="quo-list-search">
              <div className="quo-search-box">
                <FiSearch size={13} className="quo-search-icon" />
                <input placeholder="Search invoices..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
              </div>
            </div>
            <div className="quo-list-items">
              {paginated.length === 0 && <p className="quo-list-empty">No invoices found</p>}
              {paginated.map(inv => (
                <div
                  key={inv._id}
                  className={`quo-list-item ${selected?._id === inv._id ? 'quo-item-selected' : ''}`}
                  onClick={() => setSelected(inv)}
                >
                  <div className="quo-item-top">
                    <span className="quo-item-id">INV-{inv.invoiceNumber}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                  <div className="quo-item-client">{inv.clientName || inv.client?.name || '—'}</div>
                  <div className="quo-item-bottom">
                    <span className="quo-item-amount">{fmtINR(invoiceTotals(inv).grandTotal)}</span>
                  </div>
                  <div className="quo-item-date">{fmt(inv.invoiceDate)}</div>
                </div>
              ))}
            </div>
            <div className="quo-list-pagination">
              <button className="quo-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`quo-page-btn ${page === p ? 'quo-page-active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="quo-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>

          {/* RIGHT DETAIL */}
          <div className="quo-detail-col">
            <div className="quo-action-bar">
              <button className="quo-btn-primary" onClick={() => { setEditInv(null); setShowModal(true); }}>
                <FiPlus size={13} /> New Customer Invoice
              </button>
              <button className="quo-btn-outline quo-btn-pdf" onClick={downloadInvoicePDF} disabled={!selected}>
                <FiFileText size={13} color="#dc2626" /> Save PDF
              </button>
            </div>

            {!selected ? (
              <div className="quo-empty-state">
                <FiFileText size={40} color="#cbd5e1" />
                <p>Select an invoice to view details</p>
                <button className="quo-btn-primary" onClick={() => { setEditInv(null); setShowModal(true); }}>
                  <FiPlus size={13} /> New Customer Invoice
                </button>
              </div>
            ) : (() => {
              const { subtotal, cgst, sgst, igst, grandTotal } = invoiceTotals(selected);
              return (
                <div className="quo-detail-body">
                  <div className="quo-detail-header">
                    <div className="quo-detail-id-row">
                      <h2 className="quo-detail-id">Invoice # {selected.invoiceNumber}</h2>
                      <StatusBadge status={selected.status} />
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '.5rem' }}>
                        {selected.status !== 'sent' && selected.status !== 'paid' && (
                          <button className="quo-btn-sm quo-btn-outline" onClick={() => updateStatus(selected, 'sent')}>Mark Sent</button>
                        )}
                        {selected.status !== 'paid' && (
                          <button className="quo-btn-sm quo-btn-outline" style={{ color: '#16a34a', borderColor: '#bbf7d0' }} onClick={() => updateStatus(selected, 'paid')}>Mark Paid</button>
                        )}
                        <button className="quo-btn-sm quo-btn-outline" onClick={() => { setEditInv(selected); setShowModal(true); }}><FiEdit2 size={11}/> Edit</button>
                        <button className="quo-btn-sm quo-btn-outline quo-btn-pdf" onClick={downloadInvoicePDF}><FiDownload size={11}/> Save PDF</button>
                        <button className="quo-btn-sm quo-btn-outline" onClick={printInvoice}><FiPrinter size={11}/> Print</button>
                        <button className="quo-btn-sm quo-btn-outline" style={{ color: '#dc2626', borderColor: '#fecaca' }} onClick={() => setConfirmDel(selected)}><FiTrash2 size={11}/> Delete</button>
                      </div>
                    </div>
                    <div className="quo-meta-strip">
                      <div className="quo-meta-block"><span>Customer</span><strong>{selected.clientName || selected.client?.name || '—'}</strong></div>
                      <div className="quo-meta-sep" />
                      <div className="quo-meta-block"><span>Invoice Date</span><strong>{fmt(selected.invoiceDate)}</strong></div>
                      <div className="quo-meta-sep" />
                      <div className="quo-meta-block"><span>GSTIN</span><strong>{selected.clientGSTIN || '—'}</strong></div>
                      <div className="quo-meta-total">
                        <span>Grand Total</span>
                        <strong className="quo-total-big">{fmtINR(grandTotal)}</strong>
                      </div>
                    </div>
                  </div>

                  <style>{`
                    .ci-spec-table, .ci-spec-table th, .ci-spec-table td { border: 1px solid #000; }
                    .ci-spec-table { border-collapse: collapse; }
                    .ci-spec-table th { background: #f8fafc; font-size: .72rem; padding: .35rem; }
                    .ci-spec-table td { padding: .4rem; font-size: .8rem; }
                  `}</style>
                  <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
                    <table className="ci-spec-table" style={{ width: '100%', minWidth: 720 }}>
                      <thead>
                        <tr>
                          <th style={{ width: '5%' }}>SL No.</th>
                          <th>Description</th>
                          <th style={{ width: '10%' }}>HSN/SAC</th>
                          <th style={{ width: '10%' }}>Qty/Sqft</th>
                          <th style={{ width: '14%' }}>Rate</th>
                          <th style={{ width: '16%' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selected.items || []).map((it, i) => (
                          <tr key={i}>
                            <td style={{ textAlign: 'center' }}>{i + 1}</td>
                            <td>{it.description}</td>
                            <td style={{ textAlign: 'center' }}>{it.hsnCode || '—'}</td>
                            <td style={{ textAlign: 'center' }}>{it.qty}</td>
                            <td style={{ textAlign: 'center' }}>₹{fmt2(it.rate)}</td>
                            <td style={{ textAlign: 'center' }}>₹{fmt2((+it.qty || 0) * (+it.rate || 0))}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
                          <td colSpan={5} style={{ textAlign: 'right' }}>TOTAL</td>
                          <td style={{ textAlign: 'center' }}>₹{fmt2(subtotal)}</td>
                        </tr>
                        {+selected.cgstPercent > 0 && (
                          <tr><td colSpan={5} style={{ textAlign: 'right' }}>C GST {selected.cgstPercent}%</td><td style={{ textAlign: 'center' }}>₹{fmt2(cgst)}</td></tr>
                        )}
                        {+selected.sgstPercent > 0 && (
                          <tr><td colSpan={5} style={{ textAlign: 'right' }}>S GST {selected.sgstPercent}%</td><td style={{ textAlign: 'center' }}>₹{fmt2(sgst)}</td></tr>
                        )}
                        {+selected.igstPercent > 0 && (
                          <tr><td colSpan={5} style={{ textAlign: 'right' }}>I GST {selected.igstPercent}%</td><td style={{ textAlign: 'center' }}>₹{fmt2(igst)}</td></tr>
                        )}
                        <tr><td colSpan={5} style={{ textAlign: 'right' }}>ROUND OFF</td><td style={{ textAlign: 'center' }}>₹{fmt2(selected.roundOff)}</td></tr>
                        <tr style={{ fontWeight: 700, background: '#f8fafc' }}>
                          <td colSpan={5} style={{ textAlign: 'right' }}>GRAND TOTAL</td>
                          <td style={{ textAlign: 'center' }}>₹{fmt2(grandTotal)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {selected.amountInWords && (
                    <div className="quo-notes-box" style={{ marginTop: '1rem' }}>
                      <div className="quo-notes-icon">🔤</div>
                      <div>
                        <p className="quo-notes-title">Amount in Words</p>
                        <p style={{ margin: 0, fontSize: '.85rem' }}>{selected.amountInWords}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {showModal && (
        <CustomerInvoiceModal
          invoice={editInv}
          onClose={() => setShowModal(false)}
          onSaved={inv => {
            setList(prev => editInv ? prev.map(x => x._id === inv._id ? inv : x) : [inv, ...prev]);
            setSelected(inv);
            setShowModal(false);
            toast.success(editInv ? 'Invoice updated' : 'Invoice created');
          }}
        />
      )}

      {confirmDel && (
        <ConfirmDialog
          title="Delete Invoice"
          message={`Delete "INV-${confirmDel.invoiceNumber}"? This cannot be undone.`}
          confirmLabel="Delete"
          confirmColor="#dc2626"
          icon="🗑️"
          onCancel={() => setConfirmDel(null)}
          onConfirm={() => deleteInv(confirmDel._id)}
        />
      )}
    </Layout>
  );
}

/* ── New/Edit Customer Invoice Modal ── */
function CustomerInvoiceModal({ invoice, onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [customerMode, setCustomerMode] = useState(invoice?.client ? 'existing' : (invoice ? 'new' : 'existing'));
  const [form, setForm] = useState({
    client:        invoice?.client?._id || invoice?.client || '',
    clientName:    invoice?.clientName || '',
    clientAddress: invoice?.clientAddress || '',
    clientGSTIN:   invoice?.clientGSTIN || '',
    invoiceDate:   invoice?.invoiceDate ? invoice.invoiceDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
    deliveryNote:      invoice?.deliveryNote || '',
    suppliersRef:      invoice?.suppliersRef || '',
    otherReference:    invoice?.otherReference || '',
    buyersOrderNo:     invoice?.buyersOrderNo || '',
    buyersOrderDate:   invoice?.buyersOrderDate ? invoice.buyersOrderDate.slice(0, 10) : '',
    dispatchDocNo:     invoice?.dispatchDocNo || '',
    vehicleNo:         invoice?.vehicleNo || '',
    dispatchedThrough: invoice?.dispatchedThrough || 'Road',
    destination:       invoice?.destination || '',
    cgstPercent:   invoice?.cgstPercent ?? 9,
    sgstPercent:   invoice?.sgstPercent ?? 9,
    igstPercent:   invoice?.igstPercent ?? 0,
    amountInWords: invoice?.amountInWords || '',
  });
  const [items, setItems] = useState(invoice?.items?.length
    ? invoice.items.map(it => ({ description: it.description || '', hsnCode: it.hsnCode || '', qty: it.qty ?? '', rate: it.rate ?? '' }))
    : [freshItem()]
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get('/clients').then(r => setClients(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function onClientChange(id) {
    const found = clients.find(c => c._id === id);
    set('client', id);
    set('clientName', found ? found.name : '');
    set('clientAddress', found ? (found.address || '') : '');
  }

  function switchCustomerMode(mode) {
    setCustomerMode(mode);
    set('client', '');
    set('clientName', '');
    set('clientAddress', '');
    set('clientGSTIN', '');
  }

  function addItem() { setItems(p => [...p, freshItem()]); }
  function removeItem(i) { setItems(p => p.filter((_, idx) => idx !== i)); }
  function setItemField(i, k, v) { setItems(p => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it)); }

  const subtotal = items.reduce((s, it) => s + ((+it.qty || 0) * (+it.rate || 0)), 0);
  const cgst = subtotal * (+form.cgstPercent || 0) / 100;
  const sgst = subtotal * (+form.sgstPercent || 0) / 100;
  const igst = subtotal * (+form.igstPercent || 0) / 100;
  const rawTotal = subtotal + cgst + sgst + igst;
  const grandTotalPreview = Math.round(rawTotal);

  async function save() {
    if (!form.clientName.trim()) return toast.error('Customer is required');
    if (!items.length || items.some(it => !it.description.trim())) return toast.error('Every line needs a description');
    setSaving(true);
    try {
      const cleanedItems = items.map(it => ({
        ...it,
        qty: +it.qty || 0,
        rate: +it.rate || 0,
      }));
      const payload = { ...form, items: cleanedItems, client: form.client || undefined };
      const r = invoice
        ? await API.put(`/customer-invoices/${invoice._id}`, payload)
        : await API.post('/customer-invoices', payload);
      onSaved(r.data);
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="quo-overlay">
      <div className="quo-modal" style={{ maxWidth: 820 }} onClick={e => e.stopPropagation()}>
        <div className="quo-modal-header">
          <h3>{invoice ? `Edit INV-${invoice.invoiceNumber}` : 'New Customer Invoice'}</h3>
          <button className="quo-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="quo-modal-body">
          <div className="co-mrow">
            <div className="co-mgroup co-mfull">
              <label>Customer *</label>
              <div style={{ display: 'flex', gap: '.4rem', marginBottom: '.5rem' }}>
                <button
                  type="button"
                  className="quo-btn-sm quo-btn-outline"
                  style={customerMode === 'existing' ? { background: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb', fontWeight: 700 } : undefined}
                  onClick={() => switchCustomerMode('existing')}
                >Existing Customer</button>
                <button
                  type="button"
                  className="quo-btn-sm quo-btn-outline"
                  style={customerMode === 'new' ? { background: '#eff6ff', borderColor: '#bfdbfe', color: '#2563eb', fontWeight: 700 } : undefined}
                  onClick={() => switchCustomerMode('new')}
                >New Customer</button>
              </div>
              {customerMode === 'existing' ? (
                <select value={form.client} onChange={e => onClientChange(e.target.value)}>
                  <option value="">Select Customer...</option>
                  {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              ) : (
                <input value={form.clientName} onChange={e => set('clientName', e.target.value)} placeholder="Customer name" />
              )}
            </div>
          </div>
          <div className="co-mrow">
            <div className="co-mgroup co-mfull">
              <label>Customer Address</label>
              <input value={form.clientAddress} onChange={e => set('clientAddress', e.target.value)} placeholder="Billing address" />
            </div>
          </div>
          <div className="co-mrow">
            <div className="co-mgroup">
              <label>Customer GSTIN</label>
              <input value={form.clientGSTIN} onChange={e => set('clientGSTIN', e.target.value)} placeholder="e.g. 33AISPB7526Q1ZO" />
            </div>
            <div className="co-mgroup">
              <label>Invoice Date</label>
              <input type="date" value={form.invoiceDate} onChange={e => set('invoiceDate', e.target.value)} />
            </div>
          </div>

          <div className="co-items-editor-header" style={{ marginTop: '.5rem' }}><span>Dispatch Details</span></div>
          <div className="co-mrow">
            <div className="co-mgroup">
              <label>Delivery Note</label>
              <input value={form.deliveryNote} onChange={e => set('deliveryNote', e.target.value)} />
            </div>
            <div className="co-mgroup">
              <label>Supplier's Ref.</label>
              <input value={form.suppliersRef} onChange={e => set('suppliersRef', e.target.value)} />
            </div>
            <div className="co-mgroup">
              <label>Other Reference</label>
              <input value={form.otherReference} onChange={e => set('otherReference', e.target.value)} />
            </div>
          </div>
          <div className="co-mrow">
            <div className="co-mgroup">
              <label>Buyers Order No.</label>
              <input value={form.buyersOrderNo} onChange={e => set('buyersOrderNo', e.target.value)} placeholder="e.g. Email confirmation" />
            </div>
            <div className="co-mgroup">
              <label>Buyers Order Dated</label>
              <input type="date" value={form.buyersOrderDate} onChange={e => set('buyersOrderDate', e.target.value)} />
            </div>
          </div>
          <div className="co-mrow">
            <div className="co-mgroup">
              <label>Dispatch Document No.</label>
              <input value={form.dispatchDocNo} onChange={e => set('dispatchDocNo', e.target.value)} />
            </div>
            <div className="co-mgroup">
              <label>Vehicle No.</label>
              <input value={form.vehicleNo} onChange={e => set('vehicleNo', e.target.value)} />
            </div>
          </div>
          <div className="co-mrow">
            <div className="co-mgroup">
              <label>Dispatched Through</label>
              <input value={form.dispatchedThrough} onChange={e => set('dispatchedThrough', e.target.value)} placeholder="e.g. Road" />
            </div>
            <div className="co-mgroup">
              <label>Destination</label>
              <input value={form.destination} onChange={e => set('destination', e.target.value)} />
            </div>
          </div>

          <div className="co-items-editor">
            <div className="co-items-editor-header">
              <span>Line Items</span>
              <button className="co-btn-ghost" onClick={addItem}><FiPlus size={11}/> Add Item</button>
            </div>
            {items.map((it, i) => (
              <div key={i} className="co-item-row-editor">
                <div className="co-mrow">
                  <div className="co-mgroup co-mfull">
                    <label>Description</label>
                    <input value={it.description} onChange={e => setItemField(i, 'description', e.target.value)} placeholder="e.g. Fluded Glass" />
                  </div>
                  {items.length > 1 && <button className="co-remove-item" onClick={() => removeItem(i)}><FiX size={11}/></button>}
                </div>
                <div className="co-mrow">
                  <div className="co-mgroup">
                    <label>HSN/SAC</label>
                    <input value={it.hsnCode} onChange={e => setItemField(i, 'hsnCode', e.target.value)} placeholder="e.g. 7003" />
                  </div>
                  <div className="co-mgroup">
                    <label>Qty/Sqft</label>
                    <input type="number" min="0" value={it.qty} onChange={e => setItemField(i, 'qty', e.target.value)} />
                  </div>
                  <div className="co-mgroup">
                    <label>Rate (₹)</label>
                    <input type="number" min="0" value={it.rate} onChange={e => setItemField(i, 'rate', e.target.value)} />
                  </div>
                </div>
                <div style={{ textAlign: 'right', fontSize: '.78rem', fontWeight: 700, color: '#0f172a' }}>
                  Amount: {fmtINR((+it.qty || 0) * (+it.rate || 0))}
                </div>
              </div>
            ))}
          </div>

          <div className="co-mrow">
            <div className="co-mgroup">
              <label>CGST %</label>
              <input type="number" min="0" value={form.cgstPercent} onChange={e => set('cgstPercent', e.target.value)} />
            </div>
            <div className="co-mgroup">
              <label>SGST %</label>
              <input type="number" min="0" value={form.sgstPercent} onChange={e => set('sgstPercent', e.target.value)} />
            </div>
            <div className="co-mgroup">
              <label>IGST %</label>
              <input type="number" min="0" value={form.igstPercent} onChange={e => set('igstPercent', e.target.value)} />
            </div>
          </div>

          <div style={{ textAlign: 'right', fontSize: '.85rem', fontWeight: 700, color: '#0f172a', margin: '.5rem 0' }}>
            Grand Total (auto): {fmtINR(grandTotalPreview)}
          </div>

          <div className="co-mgroup">
            <label>Amount in Words (manual)</label>
            <input value={form.amountInWords} onChange={e => set('amountInWords', e.target.value)} placeholder="e.g. Rupees Three Thousand Four Hundred and Ten Only" />
          </div>
        </div>
        <div className="quo-modal-footer">
          <button className="co-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="co-btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : invoice ? 'Save Changes' : 'Create Invoice'}</button>
        </div>
      </div>
    </div>
  );
}
